/**
 * Versioned, agency-scoped rule-set configuration.
 *
 * The engine implements generic, parameterised strategies. Jurisdiction-
 * specific values (South Carolina pilot or any other) live ONLY in data that
 * conforms to these types and is stored as an immutable RuleVersion.
 */

export type ServiceMethod = 'personal' | 'substitute' | 'posting';

/** Outcomes a server can record for an in-person attempt. */
export type AttemptOutcome =
  // success
  | 'served'
  // ordinary unsuccessful attempts (may count toward non-service)
  | 'no_answer'
  | 'not_home'
  | 'gated_no_access'
  | 'other_unsuccessful'
  // immediate non-service dispositions (RULE-006)
  | 'bad_address'
  | 'outside_service_area'
  | 'moved'
  | 'vacant'
  | 'deceased'
  // requires supervisor review before non-service (RULE-006)
  | 'unable_to_locate'
  // special paper-type completions
  | 'property_documented' // Writ of Execution V1 (RULE-014)
  | 'supervisor_action'; // Writ of Assistance (RULE-013)

export const UNSUCCESSFUL_OUTCOMES: readonly AttemptOutcome[] = [
  'no_answer',
  'not_home',
  'gated_no_access',
  'other_unsuccessful',
];

export const IMMEDIATE_NON_SERVICE_OUTCOMES: readonly AttemptOutcome[] = [
  'bad_address',
  'outside_service_area',
  'moved',
  'vacant',
  'deceased',
];

export const OUTCOME_LABELS: Record<AttemptOutcome, string> = {
  served: 'Served',
  no_answer: 'No answer',
  not_home: 'Not home (per occupant)',
  gated_no_access: 'Gated / no access',
  other_unsuccessful: 'Other unsuccessful',
  bad_address: 'Bad address',
  outside_service_area: 'Outside county / service area',
  moved: 'Moved / no longer resides',
  vacant: 'Vacant house',
  deceased: 'Confirmed deceased',
  unable_to_locate: 'Unable to locate address',
  property_documented: 'Property documented',
  supervisor_action: 'Supervisor-directed action',
};

export interface TimeWindow {
  id: string;
  label: string;
  /** inclusive "HH:MM" */
  start: string;
  /** exclusive "HH:MM" */
  end: string;
}

/** Regular-paper non-service qualification (RULE-004 / RULE-005 strategy). */
export interface NonServicePolicy {
  minAttempts: number;
  /** Whole calendar days required between consecutive qualifying attempts. */
  minFullDaysBetween: number;
  /** Each window must be covered by a distinct qualifying attempt. */
  windows: TimeWindow[];
  saturdayWildcard: { enabled: boolean; maxCount: number };
}

/** Eviction attempt-spacing / posting strategy (RULE-007). */
export interface EvictionPolicy {
  minHoursBetweenAttempt1And2: number;
  minTimeOfDayDifferenceHours: number;
  /** Posting (attempt 3) allowed on any local calendar day at least this many days after attempt 2. */
  postingMinDaysAfterAttempt2: number;
}

export interface SetOutPolicy {
  /** Hours after serve/post before a set-out may be scheduled (RULE-008). */
  waitHoursAfterService: number;
}

export interface SubstituteRecipientPolicy {
  minAge: number;
  requireName: boolean;
  requireRelationship: boolean;
  requireResidesWithDefendant: boolean;
  requireCompetent: boolean;
  requireNotAParty: boolean;
}

export type PostingPolicy = 'never' | 'after_qualifying_attempts' | 'any_attempt';
export type CompletionMode = 'standard' | 'supervisor_action' | 'property_documentation';

export interface PaperTypeRules {
  code: string;
  name: string;
  description?: string;
  substituteAllowed: boolean;
  substituteRecipient?: SubstituteRecipientPolicy;
  posting: PostingPolicy;
  completion: CompletionMode;
  /** null → non-service only through immediate dispositions or supervisor override. */
  nonService: NonServicePolicy | null;
  eviction?: EvictionPolicy;
  setOut?: SetOutPolicy;
  /** Outcomes that end the paper as non-served without further attempts. */
  immediateDispositions: AttemptOutcome[];
  /** Outcomes that route the paper to supervisor review. */
  supervisorReviewDispositions: AttemptOutcome[];
  /** Days before court date after which the paper is past its service cutoff. */
  serviceCutoffDaysBeforeCourt: number;
  recipientSignature: 'none' | 'optional' | 'required';
  archived?: boolean;
}

export type CustomerClass = 'attorney' | 'magistrate' | 'court' | 'government' | 'private';

export interface FeeRule {
  paperTypeCode: string;
  customerClass: CustomerClass;
  amountCents: number;
}

export interface UrgencyPolicy {
  /** Auto-assign Late-Received Priority when received inside cutoff (ROUTE-005). */
  lateReceivedAutoPriority: boolean;
  /** Days remaining with zero attempts ⇒ supervisor alert + automatic urgent (ROUTE-006). */
  escalateAtDaysRemaining: number;
  /** Server reminder thresholds in days remaining (ROUTE-007). */
  serverReminderDays: number[];
}

export interface RuleSetConfig {
  schemaVersion: 1;
  jurisdiction: string;
  timezone: string;
  serviceHours: { start: string; end: string };
  sundayBlocked: boolean;
  holidays: { date: string; name: string }[];
  /** Whether a supervisor-overridden Sunday/holiday attempt may count toward non-service. */
  overrideAttemptsQualify: boolean;
  gpsWarningFeet: number;
  urgency: UrgencyPolicy;
  paperTypes: PaperTypeRules[];
  fees: FeeRule[];
}

export interface Issue {
  code: string;
  message: string;
}
